import { z } from "zod";
import {
    EVMTokenAddress, EVMAddress, SolAddress,
    Address, Amount, Balance, Sau, BlockNumber, Milliseconds, TxId,
    AddressFamily, AddressFamilyMap,
    BtcAddress, TonAddress, TronAddress, AleoAddress, DogeAddress, LtcAddress, XrplAddress,
    PayinMethod,
} from "@halliday-lib/schema";

// Re-export HallidayLib address & primitive types (previously via wildcard)
export {
    EVMTokenAddress, EVMAddress, SolAddress,
    Address, Amount, Balance, Sau, BlockNumber, Milliseconds, TxId,
    AddressFamily, AddressFamilyMap,
    BtcAddress, TonAddress, TronAddress, AleoAddress, DogeAddress, LtcAddress, XrplAddress,
};

// Fiat
export const Fiat = z.string().refine((fiat) => /^[a-zA-Z_]{3,}$/.test(fiat), {
    message: "Invalid fiat",
});
export type Fiat = z.infer<typeof Fiat>;

export const FiatDetails = z.object({
    issuer: z.string(),
    name: z.string(),
    alias: z.string().optional(),
    symbol: z.string(),
    decimals: z.number(),
    sign: z.string().optional(),
});
export type FiatDetails = z.infer<typeof FiatDetails>;

// Token
export const Name = z.string();
export const Symbol = z.string().regex(/^[a-zA-Z]\w{1,7}$/, {
    message: "Must be a short alpha-numeric symbol",
});
export const Decimals = z.number().min(0).max(18);

export const ImageUrl = z.string().url();
export const CoinGeckoId = z.string();

export const EVMTokenPointer = z.object({
    chain: z.string(),
    address: EVMTokenAddress,
});

export const SolTokenPointer = z.object({
    chain: z.literal("solana"),
    address: SolAddress,
});

export const FlexibleTokenPointer = z.object({
    chain: z.string(),
    address: z.string(),
});

export const TokenPointer = z.union([EVMTokenPointer, SolTokenPointer, FlexibleTokenPointer]);

export const Token = z.string().refine(
    (token) => {
        const [chain, address] = token.split(":");
        const result = TokenPointer.safeParse({ chain, address });
        return result.success;
    },
    {
        message: "Invalid token",
    },
);
export type Token = z.infer<typeof Token>;

export const TokenInfo = z.object({
    name: Name,
    symbol: Symbol,
    decimals: Decimals,
    alias: Symbol.optional(),
    image_url: ImageUrl.optional(),
    coingecko_id: CoinGeckoId.optional(),
    wrapper: Symbol.optional(),
    is_native: z.boolean().optional(),
});

export const EVMTokenDetails = EVMTokenPointer.extend(TokenInfo.shape);
export type EVMTokenDetails = z.infer<typeof EVMTokenDetails>;

export const SolTokenDetails = SolTokenPointer.extend(TokenInfo.shape);
export type SolTokenDetails = z.infer<typeof SolTokenDetails>;

export const FlexibleTokenDetails = FlexibleTokenPointer.extend(TokenInfo.shape);
export type FlexibleTokenDetails = z.infer<typeof FlexibleTokenDetails>;

export const TokenDetails = z.union([EVMTokenDetails, SolTokenDetails, FlexibleTokenDetails]);
export type TokenDetails = z.infer<typeof TokenDetails>;

// Asset
export const Asset = z.union([Token, Fiat]);
export type Asset = z.infer<typeof Asset>;
export const AssetDetails = z.union([TokenDetails, FiatDetails]);
export type AssetDetails = z.infer<typeof AssetDetails>;

export const Realm = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("chain"),
        chain: z.string(),
    }),
    z.object({
        type: z.literal("issuer"),
        issuer: z.string(),
    }),
]);
export type Realm = z.infer<typeof Realm>;

// Ramp
export { RampName } from "@halliday-lib/api";

export const RampType = z.preprocess(
    (ramp_type) => (typeof ramp_type === "string" ? ramp_type.toUpperCase() : ramp_type),
    z.enum(["ONRAMP", "OFFRAMP"]),
);
export type RampType = z.infer<typeof RampType>;

// Transaction
// TODO: confirm the fields that accommodate ethers and viem
export const TransactionRequest = z.object({
    to: EVMAddress,
    from: EVMAddress.optional(),
    nonce: z.number().optional(),
    gasLimit: z.bigint().optional(),
    gasPrice: z.bigint().optional(),
    maxPriorityFeePerGas: z.bigint().optional(),
    maxFeePerGas: z.bigint().optional(),
    data: z.string().optional(),
    value: z.bigint().optional(),
    chainId: z.number(),
});
export type TransactionRequest = z.infer<typeof TransactionRequest>;

// TODO: confirm the fields that accommodate ethers and viem
export const TransactionReceipt = z.object({
    transactionHash: z.string().optional(),
    blockHash: z.string().optional(),
    blockNumber: z.number().optional(),
    from: EVMAddress.optional(),
    to: EVMAddress.optional(),
    rawReceipt: z.any(), // Preserves the original receipt from ethers or viem
});
export type TransactionReceipt = z.infer<typeof TransactionReceipt>;

// Unsigned Solana transfer transaction handed to the host for signing. `from` is the funder /
// fee-payer address and is THE ROUTING FIELD — deliberately mirroring EVM `TransactionRequest.from`
// so the existing `transaction.from` registry lookup works for both VMs (the handler never
// deserializes the transaction, so the fee payer must be surfaced at the envelope level).
// `serializedTransaction` is the base64 unsigned v0 transaction — a string because postMessage
// crosses the iframe via structured clone.
export const SolTransactionRequest = z.object({
    from: SolAddress,
    serializedTransaction: z.string(),
});
export type SolTransactionRequest = z.infer<typeof SolTransactionRequest>;

// A Solana "receipt" is just the transaction signature; the widget does not wait for confirmation
// (payment-status polling detects the inbound transfer, as with EVM).
export const SolTransactionReceipt = z.object({ signature: z.string() });
export type SolTransactionReceipt = z.infer<typeof SolTransactionReceipt>;

// Chain
export const GenericChainConfig = z.object({
    chain_id: z.bigint(),
    network: z.string(),
    display: z.string().optional(),
    address_family: z.undefined(),
    explorer: z.url().optional(),
    image: z.url().optional(),
    is_testnet: z.boolean(),
});
export type GenericChainConfig = z.infer<typeof GenericChainConfig>;

export const EVMChainConfig = GenericChainConfig.extend({
    address_family: z.literal("EVM"),
    native_currency: z.object({
        name: z.string(),
        symbol: z.string(),
        decimals: z.number(),
    }),
    rpc: z.string().optional(),
});
export type EVMChainConfig = z.infer<typeof EVMChainConfig>;

export const SolChainConfig = GenericChainConfig.extend({
    address_family: z.literal("SOL"),
});
export type SolChainConfig = z.infer<typeof SolChainConfig>;

export const ChainConfig = z.discriminatedUnion("address_family", [EVMChainConfig, SolChainConfig, GenericChainConfig]);
export type ChainConfig = z.infer<typeof ChainConfig>;

export const OnrampMethod = PayinMethod;
export type OnrampMethod = z.infer<typeof OnrampMethod>;
